import { create } from 'zustand';
import { persist } from 'zustand/middleware';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Default permissions for unauthenticated or old users
const DEFAULT_PERMISSIONS = {
  can_edit_clients: true,
  can_validate_quote: false,
  can_manage_products: true,
  can_view_prices: true,
  can_manage_team: false,
  can_create_events: true,
  can_delete_quotes: false,
};

/// Auth Store
export const useAuthStore = create(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,
      permissions: DEFAULT_PERMISSIONS,

      login: async (email, password) => {
        set({ isLoading: true, error: null });
        try {
          const res = await fetch(`${API_URL}/auth/login`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email, password }),
          });
          const data = await res.json();
          if (!res.ok) throw new Error(data.detail || 'Login failed');
          set({ 
            user: data, 
            token: data.token, 
            isAuthenticated: true, 
            isLoading: false,
            permissions: data.permissions || DEFAULT_PERMISSIONS
          });
          return data;
        } catch (error) {
          set({ error: error.message, isLoading: false });
          throw error;
        }
      },

      register: async (userData) => {
        set({ isLoading: true, error: null });
        try {
          const res = await fetch(`${API_URL}/auth/register`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(userData),
          });
          const data = await res.json();
          if (!res.ok) throw new Error(data.detail || 'Registration failed');

          // --- MODIFICATION ICI ---
          // On ne connecte l'utilisateur que s'il est actif (ex: premier admin)
          if (data.is_active) {
             set({ 
              user: data, 
              token: data.token, 
              isAuthenticated: true, 
              isLoading: false,
              permissions: data.permissions || DEFAULT_PERMISSIONS
            });
          } else {
            // Si l'utilisateur est inactif (staff en attente), on arrête le chargement
            // MAIS on ne met pas isAuthenticated à true et on ne stocke pas le token.
            set({ isLoading: false });
          }
          // ------------------------

          return data;
        } catch (error) {
          set({ error: error.message, isLoading: false });
          throw error;
        }
      },

      logout: () => {
        set({ user: null, token: null, isAuthenticated: false, error: null, permissions: DEFAULT_PERMISSIONS });
      },

      // Permission check helpers
      can: (permission) => {
        const perms = get().permissions || DEFAULT_PERMISSIONS;
        return perms[permission] === true;
      },

      isAdmin: () => {
        const user = get().user;
        return user?.role === 'admin';
      },

      getHeaders: () => {
        const token = get().token;
        return {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` }),
        };
      },
    }),
    { name: 'eventpro-auth' }
  )
);

// Theme Store
export const useThemeStore = create(
  persist(
    (set) => ({
      theme: 'dark',
      toggleTheme: () => set((state) => ({ theme: state.theme === 'dark' ? 'light' : 'dark' })),
      setTheme: (theme) => set({ theme }),
    }),
    { name: 'eventpro-theme' }
  )
);

// UI Store
export const useUIStore = create((set) => ({
  sidebarOpen: false,
  searchOpen: false,
  setSidebarOpen: (open) => set({ sidebarOpen: open }),
  setSearchOpen: (open) => set({ searchOpen: open }),
  toggleSidebar: () => set((state) => ({ sidebarOpen: !state.sidebarOpen })),
}));

// Features Store (system config / feature flags)
export const useFeaturesStore = create(
  persist(
    (set) => ({
      crm: false,
      gold: false,
      loaded: false,
      loadFeatures: async () => {
        try {
          const res = await fetch(`${API_URL}/system/config`);
          if (res.ok) {
            const data = await res.json();
            set({ crm: data.crm_enabled === true, gold: data.gold_enabled === true, loaded: true });
          } else {
            set({ loaded: true });
          }
        } catch {
          set({ loaded: true });
        }
      },
    }),
    { name: 'eventpro-features' }
  )
);
